import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Pencil, Trash2, Search } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useRef } from "react";
import Logo from "../../src/images/logo.png";
import autoTable from "jspdf-autotable";

export default function Invoices() {
  const navigate = useNavigate();
  const invoiceRef = useRef();

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [invoicelist, setInvoicelist] = useState([]);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");

  const [invoiceHeader, setInvoiceHeader] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  //Fetch Invoices
  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = () => {
    axios
      .get("http://tntyping.com/Adminconsole/api/nutsinvoicelist")
      .then((response) => {
        if (response.data.errorCode === 2) {
          Swal.fire({
            icon: "error",
            text: response.data.Message,
            width: 320,
            padding: "1em",
            confirmButtonText: "OK"
          });
        }

        if (response.data.errorCode === 0) {
          const list = response.data.result || [];
          setInvoicelist(list);

          //Create unique customer list
          const uniqueCustomers = [...new Set(list.map((i) => i.customername))];
          setCustomers(uniqueCustomers);
        }
      });
  };

  //Open Invoice Modal
  const goInvoicepage = (id, invoiceno) => {
    setLoading(true);

    axios
      .get(
        `http://tntyping.com/Adminconsole/api/nutsinvoicelistbyid?invoiceno=${invoiceno}`
      )
      .then((response) => {
        if (response.data.errorCode === 0) {
          setInvoiceHeader(response.data.result); //invoice header
          setInvoiceDetails(response.data.items || []); //products
          setInvoiceNo(invoiceno);
          setShowModal(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  //Create Invoice
  const createInvoice = () => {
    navigate("/sales");
  };

  //Edit Invoice
  const editInvoice = (invoiceno) => {
    navigate("/editinvoice", {
      state: { currentinvoiceno: invoiceno }
    });
  };

  const deleteInvoice = (invoiceno) => {
    Swal.fire({
      text: "Delete Invoice?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
      width: 320,
      padding: "1em",
      customClass: {
        popup: "rounded-xl text-sm"
      }
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .get(
            `http://tntyping.com/Adminconsole/api/deletenutsinvoice?invoiceno=${invoiceno}`
          )
          .then((response) => {
            if (response.data.errorCode === 0) {
              Swal.fire({
                icon: "success",
                text: "Invoice Deleted Successfully",
                timer: 1500,
                showConfirmButton: false,
                width: 320,
                padding: "1em",
                confirmButtonText: "OK",
                customClass: {
                  popup: "rounded-xl text-sm"
                }
              });

              fetchInvoices(); // reload list
            }

            if (response.data.errorCode === 2) {
              Swal.fire({
                icon: "error",
                text: response.data.Message
              });
            }
          });
      }
    });
  };

  const filteredInvoices = invoicelist.filter((item) => {
    const matchesSearch =
      item.invoiceno.toLowerCase().includes(search.toLowerCase()) ||
      item.customername.toLowerCase().includes(search.toLowerCase());

    const matchesCustomer =
      selectedCustomer === "" || item.customername === selectedCustomer;

    return matchesSearch && matchesCustomer;
  });

  const exportPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();

    /* ------------------ LOGO ------------------ */

    const img = new Image();
    img.src = Logo;

    await new Promise((resolve) => {
      img.onload = resolve;
    });

    pdf.addImage(img, "PNG", 14, 10, 40, 18);

    /* ------------------ COMPANY NAME ------------------ */

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.text("Nuts", pageWidth - 14, 18, { align: "right" });

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.text("Retail & Wholesale", pageWidth - 14, 24, { align: "right" });

    /* ------------------ TITLE ------------------ */

    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text("INVOICE", pageWidth / 2, 40, { align: "center" });

    /* ------------------ DATE TIME ------------------ */

    const now = new Date();
    const dateTime = now.toLocaleString();

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");

    pdf.text(`Invoice No : ${invoiceNo}`, 14, 55);
    pdf.text(`Generated : ${dateTime}`, 14, 62);

    /* ------------------ CUSTOMER DETAILS ------------------ */

    pdf.text(`Customer : ${invoiceHeader?.customername}`, 120, 55);
    pdf.text(`Payment : ${invoiceHeader?.paymenttype}`, 120, 62);
    pdf.text(`Due Date : ${formatDate(invoiceHeader?.duedate)}`, 120, 69);

    /* ------------------ TABLE DATA ------------------ */

    const tableRows = [];

    invoiceDetails.forEach((item) => {
      let productName = item.productname;

      if (item.batchno) {
        productName += `\nBatch : ${item.batchno}`;
      }

      if (item.manufacturingdate && item.manufacturingdate !== "0000-00-00") {
        productName += `\nMFG : ${formatDate(item.manufacturingdate)}`;
      }

      if (item.expirydate && item.expirydate !== "0000-00-00") {
        productName += `\nEXP : ${formatDate(item.expirydate)}`;
      }

      tableRows.push([
        productName,
        item.quantity,
        `Rs. ${item.price}`,
        `Rs. ${item.total}`
      ]);
    });

    /* ------------------ TABLE ------------------ */

    autoTable(pdf, {
      startY: 80,

      head: [["Product", "Qty", "Price", "Total"]],
      body: tableRows,

      theme: "grid",

      styles: {
        fontSize: 10,
        cellPadding: 4
      },

      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        halign: "center"
      },

      columnStyles: {
        0: { cellWidth: 90 },
        1: { halign: "center", cellWidth: 20 },
        2: { halign: "right", cellWidth: 35 },
        3: { halign: "right", cellWidth: 35 }
      }
    });

    /* ------------------ TOTAL CALCULATION ------------------ */

    const subtotal = invoiceDetails.reduce(
      (sum, item) => sum + Number(item.total),
      0
    );

    const finalY = pdf.lastAutoTable.finalY + 10;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);

    pdf.text(`Subtotal : Rs. ${subtotal}`, pageWidth - 14, finalY, {
      align: "right"
    });

    pdf.setFontSize(14);
    pdf.text(`Total : Rs. ${subtotal}`, pageWidth - 14, finalY + 8, {
      align: "right"
    });

    /* ------------------ FOOTER ------------------ */

    pdf.setDrawColor(200);
    pdf.line(14, 270, pageWidth - 14, 270);

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");

    pdf.text("Thank you for your business!", pageWidth / 2, 278, {
      align: "center"
    });

    /* ------------------ SAVE ------------------ */

    pdf.save(`Invoice-${invoiceNo}.pdf`);
  };

  const getCurrentDateTime = () => {
    const now = new Date();

    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();

    const hours = now.getHours().toString().padStart(2, "0");
    const minutes = now.getMinutes().toString().padStart(2, "0");

    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}-${month}-${year}`;
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Invoices</h2>
          <p className="text-sm text-gray-500">Manage your Invoices</p>
        </div>

        <button
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg shadow"
          onClick={createInvoice}
        >
          Create Invoice
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-4 items-center">
        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-3 text-gray-400" />

          <input
            type="text"
            placeholder="Search invoice..."
            className="pl-9 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Customer Dropdown */}
        <select
          value={selectedCustomer}
          onChange={(e) => setSelectedCustomer(e.target.value)}
          className="border px-4 py-2 rounded-lg bg-white shadow-sm"
        >
          <option value="">All Customers</option>

          {customers.map((cust, index) => (
            <option key={index} value={cust}>
              {cust}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
            <tr>
              <th className="p-4 text-left">Invoice No</th>
              <th className="p-4 text-left">Customer</th>
              <th className="p-4 text-left">Payment</th>
              <th className="p-4 text-left">Due Date</th>
              <th className="p-4 text-left">Total</th>
              <th className="p-4 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredInvoices.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="p-4">
                  <button
                    className="text-blue-600 hover:underline"
                    onClick={() => goInvoicepage(item.id, item.invoiceno)}
                  >
                    {item.invoiceno}
                  </button>
                </td>

                <td className="p-4">{item.customername}</td>
                <td className="p-4">{item.paymenttype}</td>
                <td className="p-4">{formatDate(item.duedate)}</td>

                <td className="p-4 font-semibold">
                  ₹{" "}
                  {(item.items || []).reduce((s, p) => s + Number(p.total), 0)}
                </td>

                <td className="p-4 flex justify-center gap-4">
                  <button
                    className="text-blue-600"
                    onClick={() => editInvoice(item.invoiceno)}
                  >
                    <Pencil size={18} />
                  </button>

                  <button
                    className="text-red-500"
                    onClick={() => deleteInvoice(item.invoiceno)}
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
            <div className="bg-white rounded-xl shadow-lg w-[700px] p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Invoice : {invoiceNo}</h3>

                <div className="flex gap-2">
                  <button
                    onClick={exportPDF}
                    className="bg-blue-600 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Export PDF
                  </button>

                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* THIS PART WILL EXPORT TO PDF */}
              <div
                ref={invoiceRef}
                className="bg-white p-8 text-gray-800"
                style={{ fontFamily: "Arial, sans-serif" }}
              >
                {/* LOGO */}
                <div className="flex justify-center mb-4">
                  <img src={Logo} alt="logo" className="h-16 object-contain" />
                </div>

                {/* TITLE */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-blue-700">INVOICE</h2>
                </div>

                {/* Invoice Info */}
                <div className="flex justify-between mb-6 text-sm">
                  <div>
                    <p>
                      <strong>Invoice No :</strong> {invoiceNo}
                    </p>

                    <p>
                      <strong>Invoice Date :</strong> {getCurrentDateTime()}
                    </p>
                  </div>

                  {invoiceHeader && (
                    <div className="text-right">
                      <p>
                        <strong>Customer :</strong> {invoiceHeader.customername}
                      </p>

                      <p>
                        <strong>Payment :</strong> {invoiceHeader.paymenttype}
                      </p>

                      <p>
                        <strong>Due Date :</strong>{" "}
                        {formatDate(invoiceHeader.duedate)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Product Table */}
                <table className="w-full border text-sm">
                  <thead className="bg-blue-50 text-gray-700">
                    <tr>
                      <th className="border p-2 text-left">Product</th>
                      <th className="border p-2 text-center">Qty</th>
                      <th className="border p-2 text-center">Price</th>
                      <th className="border p-2 text-right">Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {invoiceDetails.map((item) => (
                      <tr key={item.id}>
                        <td className="border p-2">
                          <div className="font-medium">{item.productname}</div>

                          {item.batchno && (
                            <div className="text-xs text-gray-600 mt-1">
                              <div className="bg-blue-100 text-blue-700 inline-block px-2 py-0.5 rounded">
                                Batch : {item.batchno}
                              </div>

                              {item.manufacturingdate !== "0000-00-00" && (
                                <div>
                                  MFG : {formatDate(item.manufacturingdate)}
                                </div>
                              )}

                              {item.expirydate !== "0000-00-00" && (
                                <div>EXP : {formatDate(item.expirydate)}</div>
                              )}
                            </div>
                          )}
                        </td>

                        <td className="border p-2 text-center">{item.qty}</td>

                        <td className="border p-2 text-center">
                          ₹ {item.price}
                        </td>

                        <td className="border p-2 text-right font-semibold">
                          ₹ {item.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Total */}
                <div className="flex justify-end mt-6">
                  <div className="text-lg font-bold border-t pt-3">
                    Total : ₹{" "}
                    {invoiceDetails.reduce(
                      (sum, item) => sum + Number(item.total),
                      0
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="text-center text-xs text-gray-400 mt-10">
                  Thank you for your business
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
